import { rectFromPoints } from '../core/math';
import type { Point, Rect, SelectionCombineMode } from '../core/types';
import { selectionImage } from '../engine/selectionOps';
import { growCoverage, type ColorSelectImage } from '../selection/colorSelect';
import { buildEdgeField, tracePath, type EdgeField } from '../selection/liveWire';
import { rasterizeEllipse, rasterizePolygon, rasterizeRect } from '../selection/shapes';
import { applySelectionShape, deselect } from '../state/actions';
import {
  beginSelectionSession,
  endSelectionSession,
  magicWandAt,
  updateSelectionCoverage,
} from '../state/selectionActions';
import type { OverlayContext, Tool, ToolContext, ToolPointerEvent } from './types';

function combineMode(event: ToolPointerEvent, ctx: ToolContext): SelectionCombineMode {
  if (event.shift && event.alt) return 'intersect';
  if (event.shift) return 'add';
  if (event.alt) return 'subtract';
  return ctx.options.selectionMode;
}

/** Empty New-selection gesture clears marching ants, as Photoshop does. */
function clearSelectionIfReplacing(mode: SelectionCombineMode): void {
  if (mode !== 'replace') return;
  deselect();
}

function constrainRect(start: Point, current: Point, square: boolean, fromCenter: boolean): Rect {
  let end = current;
  if (square) {
    const size = Math.max(Math.abs(current.x - start.x), Math.abs(current.y - start.y));
    end = {
      x: start.x + Math.sign(current.x - start.x || 1) * size,
      y: start.y + Math.sign(current.y - start.y || 1) * size,
    };
  }
  if (fromCenter) {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    return rectFromPoints({ x: start.x - dx, y: start.y - dy }, end);
  }
  return rectFromPoints(start, end);
}

function drawMarquee(
  overlay: { ctx: CanvasRenderingContext2D; toScreen: (p: Point) => Point },
  rect: Rect,
): void {
  const a = overlay.toScreen({ x: rect.x, y: rect.y });
  const b = overlay.toScreen({ x: rect.x + rect.width, y: rect.y + rect.height });
  const { ctx } = overlay;
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = 'rgba(0,0,0,0.8)';
  ctx.lineWidth = 1;
  ctx.strokeRect(a.x + 0.5, a.y + 0.5, b.x - a.x, b.y - a.y);
  ctx.setLineDash([4, 4]);
  ctx.lineDashOffset = 4;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.strokeRect(a.x + 0.5, a.y + 0.5, b.x - a.x, b.y - a.y);
  ctx.restore();
}

function createMarqueeTool(kind: 'rect' | 'ellipse'): Tool {
  let start: Point | null = null;
  let current: Point | null = null;
  let mode: SelectionCombineMode = 'replace';

  return {
    id: kind === 'rect' ? 'selectRect' : 'selectEllipse',
    cursor: 'crosshair',
    snaps: true,
    hint: 'Click to deselect; Shift adds, Alt subtracts, Shift+Alt intersects',

    onPointerDown(event, ctx) {
      start = event.point;
      current = event.point;
      mode = combineMode(event, ctx);
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      if (!start) return;
      current = event.point;
      ctx.requestRender();
    },

    onPointerUp(event, ctx) {
      if (!start || !current) return;
      const rect = constrainRect(start, event.point, event.shift, event.alt && !event.shift);
      start = null;
      current = null;

      if (rect.width < 1 || rect.height < 1) {
        clearSelectionIfReplacing(mode);
        ctx.requestRender();
        return;
      }
      const raster =
        kind === 'rect'
          ? rasterizeRect(rect, ctx.doc.width, ctx.doc.height)
          : rasterizeEllipse(rect, ctx.doc.width, ctx.doc.height);
      if (raster) applySelectionShape(raster, mode);
      ctx.requestRender();
    },

    cancel(ctx) {
      start = null;
      current = null;
      ctx.requestRender();
    },

    drawOverlay(overlay) {
      if (!start || !current) return;
      const rect = rectFromPoints(start, current);
      if (kind === 'rect') {
        drawMarquee(overlay, rect);
        return;
      }
      const a = overlay.toScreen({ x: rect.x, y: rect.y });
      const b = overlay.toScreen({ x: rect.x + rect.width, y: rect.y + rect.height });
      const { ctx } = overlay;
      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.ellipse(
        (a.x + b.x) / 2,
        (a.y + b.y) / 2,
        Math.abs(b.x - a.x) / 2,
        Math.abs(b.y - a.y) / 2,
        0,
        0,
        Math.PI * 2,
      );
      ctx.stroke();
      ctx.restore();
    },
  };
}

export const selectRectTool = createMarqueeTool('rect');
export const selectEllipseTool = createMarqueeTool('ellipse');

/** Dashed polyline through document points, the shared look of every lasso. */
function drawPath(overlay: OverlayContext, points: readonly Point[], close: boolean): void {
  if (points.length < 2) return;
  const { ctx } = overlay;
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  const first = overlay.toScreen(points[0]);
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i < points.length; i += 1) {
    const p = overlay.toScreen(points[i]);
    ctx.lineTo(p.x, p.y);
  }
  if (close) ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

function drawAnchors(overlay: OverlayContext, points: readonly Point[]): void {
  const { ctx } = overlay;
  ctx.save();
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.strokeStyle = 'rgba(0,0,0,0.8)';
  for (const point of points) {
    const p = overlay.toScreen(point);
    ctx.beginPath();
    ctx.rect(p.x - 2, p.y - 2, 4, 4);
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

/** Shift constrains a segment to the eight directions, as every lasso does. */
function constrainToAxes(from: Point, to: Point): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const angle = (Math.round(Math.atan2(dy, dx) / (Math.PI / 4)) * Math.PI) / 4;
  const length = Math.hypot(dx, dy);
  return { x: from.x + Math.cos(angle) * length, y: from.y + Math.sin(angle) * length };
}

const LASSO_MIN_DISTANCE = 1.5;

export const lassoTool: Tool = (() => {
  let points: Point[] = [];
  let mode: SelectionCombineMode = 'replace';

  return {
    id: 'lasso',
    cursor: 'crosshair',
    hint: 'Click to deselect; draw an outline to select',

    onPointerDown(event, ctx) {
      points = [event.point];
      mode = combineMode(event, ctx);
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      if (points.length === 0) return;
      const last = points[points.length - 1];
      if (Math.hypot(event.point.x - last.x, event.point.y - last.y) < LASSO_MIN_DISTANCE) return;
      points.push(event.point);
      ctx.requestRender();
    },

    onPointerUp(_event, ctx) {
      const path = points;
      points = [];
      if (path.length >= 3) {
        const raster = rasterizePolygon(path, ctx.doc.width, ctx.doc.height);
        if (raster) applySelectionShape(raster, mode);
      } else {
        clearSelectionIfReplacing(mode);
      }
      ctx.requestRender();
    },

    cancel(ctx) {
      points = [];
      ctx.requestRender();
    },

    drawOverlay(overlay) {
      drawPath(overlay, points, true);
    },
  };
})();

/** How close to the first anchor a click has to land to close the outline. */
const CLOSE_DISTANCE = 6;

/**
 * A lasso built from clicked anchors. The outline stays open between clicks, so
 * it also needs a keyboard: Enter closes it, Backspace drops the last anchor and
 * Escape throws the whole thing away.
 */
export const polygonLassoTool: Tool = (() => {
  let anchors: Point[] = [];
  let hover: Point | null = null;
  let mode: SelectionCombineMode = 'replace';

  function reset(): void {
    anchors = [];
    hover = null;
  }

  function commit(ctx: ToolContext): void {
    const path = anchors;
    reset();
    if (path.length >= 3) {
      const raster = rasterizePolygon(path, ctx.doc.width, ctx.doc.height);
      if (raster) applySelectionShape(raster, mode);
    }
    ctx.requestRender();
  }

  return {
    id: 'lassoPolygon',
    cursor: 'crosshair',
    hint: 'Click to add points, Enter or click the first point to close, Backspace undoes a point',

    onPointerDown(event, ctx) {
      if (anchors.length === 0) {
        mode = combineMode(event, ctx);
        anchors = [event.point];
        hover = event.point;
        ctx.requestRender();
        return;
      }

      const first = anchors[0];
      const nearFirst =
        anchors.length >= 3 &&
        Math.hypot(event.point.x - first.x, event.point.y - first.y) * ctx.view.zoom <
          CLOSE_DISTANCE;
      if (nearFirst) {
        commit(ctx);
        return;
      }
      const last = anchors[anchors.length - 1];
      anchors.push(event.shift ? constrainToAxes(last, event.point) : event.point);
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      if (anchors.length === 0) return;
      const last = anchors[anchors.length - 1];
      hover = event.shift ? constrainToAxes(last, event.point) : event.point;
      ctx.requestRender();
    },

    onDoubleClick(_event, ctx) {
      commit(ctx);
    },

    onKeyDown(event, ctx) {
      if (anchors.length === 0) return false;
      if (event.key === 'Enter') {
        commit(ctx);
        return true;
      }
      if (event.key === 'Backspace') {
        anchors.pop();
        if (anchors.length === 0) hover = null;
        ctx.requestRender();
        return true;
      }
      return false;
    },

    cancel(ctx) {
      reset();
      ctx.requestRender();
    },

    drawOverlay(overlay) {
      const preview = hover && anchors.length > 0 ? [...anchors, hover] : anchors;
      drawPath(overlay, preview, anchors.length >= 3);
      drawAnchors(overlay, anchors);
    },
  };
})();

/**
 * Magnetic lasso. The edge field is built once per outline from the flattened
 * image, then every pointer move runs a live wire from the last anchor to the
 * pointer; once that wire grows past the frequency setting its end becomes a new
 * anchor, which is what keeps the search boxes small.
 */
export const magneticLassoTool: Tool = (() => {
  let field: EdgeField | null = null;
  let anchors: Point[] = [];
  let committed: Point[] = [];
  let wire: Point[] = [];
  let mode: SelectionCombineMode = 'replace';

  function reset(): void {
    field = null;
    anchors = [];
    committed = [];
    wire = [];
  }

  function outline(): Point[] {
    return [...committed, ...wire];
  }

  function commit(ctx: ToolContext): void {
    const path = outline();
    reset();
    if (path.length >= 3) {
      const raster = rasterizePolygon(path, ctx.doc.width, ctx.doc.height);
      if (raster) applySelectionShape(raster, mode);
    }
    ctx.requestRender();
  }

  return {
    id: 'lassoMagnetic',
    cursor: 'crosshair',
    hint: 'Trace an edge, click to pin a point, Enter closes, Backspace undoes',

    onPointerDown(event, ctx) {
      if (anchors.length === 0) {
        const image = selectionImage(ctx.engine, ctx.doc, true);
        if (!image) return;
        field = buildEdgeField(image);
        mode = combineMode(event, ctx);
        anchors = [event.point];
        committed = [event.point];
        wire = [];
        ctx.requestRender();
        return;
      }

      const first = anchors[0];
      if (
        anchors.length >= 2 &&
        Math.hypot(event.point.x - first.x, event.point.y - first.y) * ctx.view.zoom <
          CLOSE_DISTANCE
      ) {
        commit(ctx);
        return;
      }
      committed = outline();
      anchors.push(committed[committed.length - 1] ?? event.point);
      wire = [];
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      if (!field || anchors.length === 0) return;
      const last = anchors[anchors.length - 1];
      wire = tracePath(field, last, event.point, {
        radius: ctx.options.magneticWidth,
        contrast: ctx.options.magneticContrast,
      });

      // A wire longer than the frequency setting is pinned so it stops shifting.
      if (wire.length > Math.max(4, ctx.options.magneticFrequency)) {
        committed = outline();
        anchors.push(committed[committed.length - 1]);
        wire = [];
      }
      ctx.requestRender();
    },

    onDoubleClick(_event, ctx) {
      commit(ctx);
    },

    onKeyDown(event, ctx) {
      if (anchors.length === 0) return false;
      if (event.key === 'Enter') {
        commit(ctx);
        return true;
      }
      if (event.key === 'Backspace' && anchors.length > 1) {
        anchors.pop();
        const kept = anchors[anchors.length - 1];
        const index = committed.lastIndexOf(kept);
        if (index >= 0) committed = committed.slice(0, index + 1);
        wire = [];
        ctx.requestRender();
        return true;
      }
      return false;
    },

    cancel(ctx) {
      reset();
      ctx.requestRender();
    },

    drawOverlay(overlay) {
      const path = outline();
      drawPath(overlay, path, path.length >= 3);
      drawAnchors(overlay, anchors);
    },
  };
})();

export const magicWandTool: Tool = {
  id: 'magicWand',
  cursor: 'crosshair',
  hint: 'Click a colour; Shift adds, Alt subtracts',

  onPointerDown(event, ctx) {
    magicWandAt(event.point, combineMode(event, ctx));
    ctx.requestRender();
  },
};

/** Filled circle of coverage over the whole document, the seed of one drag step. */
function discCoverage(width: number, height: number, center: Point, radius: number): Uint8Array {
  const coverage = new Uint8Array(width * height);
  const minX = Math.max(0, Math.floor(center.x - radius));
  const maxX = Math.min(width - 1, Math.ceil(center.x + radius));
  const minY = Math.max(0, Math.floor(center.y - radius));
  const maxY = Math.min(height - 1, Math.ceil(center.y + radius));
  const squared = radius * radius;

  for (let y = minY; y <= maxY; y += 1) {
    for (let x = minX; x <= maxX; x += 1) {
      const dx = x + 0.5 - center.x;
      const dy = y + 0.5 - center.y;
      if (dx * dx + dy * dy <= squared) coverage[y * width + x] = 255;
    }
  }
  return coverage;
}

/**
 * Quick Selection. Each drag step grows the brushed disc into the colours around
 * it and unions the result into what the drag has gathered so far. The mask is
 * updated live but only one history entry is recorded for the whole drag.
 */
export const quickSelectTool: Tool = (() => {
  let image: ColorSelectImage | null = null;
  let gathered: Uint8Array | null = null;
  let mode: SelectionCombineMode = 'replace';
  let last: Point | null = null;
  let cursor: Point | null = null;
  let radius = 15;

  function step(point: Point, ctx: ToolContext): void {
    if (!image || !gathered) return;
    const disc = discCoverage(image.width, image.height, point, radius);
    const grown = growCoverage(image, disc, ctx.options.wandTolerance);
    for (let i = 0; i < gathered.length; i += 1) {
      if (grown[i] > gathered[i]) gathered[i] = grown[i];
    }
    updateSelectionCoverage(gathered, mode, ctx.options.selectionFeather);
  }

  return {
    id: 'quickSelect',
    cursor: 'crosshair',
    hint: 'Drag over what you want; Alt drags subtract',

    onPointerDown(event, ctx) {
      image = selectionImage(ctx.engine, ctx.doc, true);
      if (!image) return;
      gathered = new Uint8Array(image.width * image.height);
      radius = Math.max(1, ctx.options.quickSelectSize / 2);
      // A drag refines one selection, so Alt means subtract rather than a new one.
      mode = event.alt ? 'subtract' : event.shift ? 'add' : ctx.options.selectionMode;
      last = event.point;
      beginSelectionSession();
      step(event.point, ctx);
    },

    onPointerMove(event, ctx) {
      cursor = event.point;
      if (!gathered || !last) {
        ctx.requestRender();
        return;
      }
      if (Math.hypot(event.point.x - last.x, event.point.y - last.y) < radius / 2) return;
      last = event.point;
      step(event.point, ctx);
    },

    onPointerUp(_event, ctx) {
      if (!gathered) return;
      image = null;
      gathered = null;
      last = null;
      endSelectionSession('Quick Selection');
      ctx.requestRender();
    },

    cancel(ctx) {
      image = null;
      gathered = null;
      last = null;
      endSelectionSession('Quick Selection');
      ctx.requestRender();
    },

    drawOverlay(overlay, ctx) {
      if (!cursor) return;
      const center = overlay.toScreen(cursor);
      const { ctx: c } = overlay;
      c.save();
      c.strokeStyle = 'rgba(255,255,255,0.9)';
      c.lineWidth = 1;
      c.beginPath();
      c.arc(center.x, center.y, (ctx.options.quickSelectSize / 2) * overlay.zoom, 0, Math.PI * 2);
      c.stroke();
      c.restore();
    },
  };
})();
